/**
 * ローカル Zip 作業コピーのハイバネート（ページ再読込・DB 切替後の再開用）。
 * 正本はあくまで Zip ファイル（FSAA ハンドル）だが、manual-download 時は
 * メモリ消失を防ぐためスナップショットを IndexedDB に退避する。
 */

import type { SampleLocalSnapshot } from './sampleLocalDb';
import { normalizeSampleLocalSnapshot } from './sampleLocalDb';

const DB_NAME = 'sample-manager-zip-session';
const STORE_NAME = 'hibernate';
const SESSION_KEY = 'active';
const DB_VERSION = 1;

/** sampleZipFile の SampleZipAutoSaveMode と同一（循環 import 回避） */
export type SampleZipHibernateAutoSaveMode = 'file-handle' | 'manual-download';

export type SampleZipHibernatedSession = {
  version: 1;
  fileName: string;
  dirty: boolean;
  autoSaveMode: SampleZipHibernateAutoSaveMode;
  snapshot: SampleLocalSnapshot;
  updatedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('idb_open_failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

export async function loadHibernatedSampleZipSession(): Promise<SampleZipHibernatedSession | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(SESSION_KEY);
      req.onsuccess = () => {
        const raw = req.result;
        if (!raw || typeof raw !== 'object') {
          resolve(null);
          return;
        }
        const o = raw as SampleZipHibernatedSession;
        resolve({
          version: 1,
          fileName: typeof o.fileName === 'string' && o.fileName.trim() ? o.fileName.trim() : 'sample-manager.zip',
          dirty: !!o.dirty,
          autoSaveMode: o.autoSaveMode === 'file-handle' ? 'file-handle' : 'manual-download',
          snapshot: normalizeSampleLocalSnapshot(o.snapshot),
          updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : new Date().toISOString(),
        });
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

export async function saveHibernatedSampleZipSession(
  session: Omit<SampleZipHibernatedSession, 'version' | 'updatedAt'> & { updatedAt?: string }
): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  const payload: SampleZipHibernatedSession = {
    version: 1,
    fileName: session.fileName,
    dirty: session.dirty,
    autoSaveMode: session.autoSaveMode,
    snapshot: normalizeSampleLocalSnapshot(session.snapshot),
    updatedAt: session.updatedAt ?? new Date().toISOString(),
  };
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(payload, SESSION_KEY);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // private mode 等
  }
}

export async function clearHibernatedSampleZipSession(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(SESSION_KEY);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}

export async function peekHibernatedSampleZipResume(): Promise<{ fileName: string; updatedAt: string } | null> {
  const s = await loadHibernatedSampleZipSession();
  if (!s) return null;
  return { fileName: s.fileName, updatedAt: s.updatedAt };
}
